import { Box, useDisclosure, useToast } from '@chakra-ui/react';
import { QueryWrapperNext } from '@diamond/shared/data-access';
import { Account, ProductKeyword } from '@diamond/shared/types';
import { Modal, showToast } from '@diamond/shared/ui';
import { apiInstance } from '@diamond/shared/utils';
import {
  useAuthentication,
  useAuthStore,
  useNotificationCount,
  usePwaStore,
  useShipToStore,
} from '@diamond/sol-buyer-context';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { setCookie } from 'cookies-next';
import { sort } from 'fast-sort';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { ReactNode, useMemo, useState } from 'react';
import { useInterval } from 'usehooks-ts';
import shallow from 'zustand/shallow';

import {
  BOTTOM_NAV_HEIGHT,
  shouldShowBuyerBottomNav,
} from './bottom-nav/bottom-nav-config';
import { BuyerBottomNav } from './bottom-nav/BuyerBottomNav';
import Footer from './Footer';
import {
  BUYER_LAYOUT_HORIZONTAL_PADDING,
  PageContainer,
} from './layout-constants';
import { OptionShipTo } from './OptionShipTo';

interface BuyerLayoutProps {
  children: ReactNode;
  containerClassname?: string;
}

const NavHeaderNoSSR = dynamic(() => import('./NavHeader'), {
  ssr: false,
});

export function BuyerLayout({
  children,
  containerClassname,
}: BuyerLayoutProps) {
  const router = useRouter();
  const access_token = useAuthStore((state) => state.access_token);
  const isPwaOffline = usePwaStore((state) => state.isPwaOffline);

  const [
    selectedShipTo,
    selectedShipToId,
    availableShipTo,
    setAvailableShipTo,
    setSelectedShipToId,
    setSelectedShipTo,
    setSelectedShipToDc,
  ] = useShipToStore(
    (s) => [
      s.selectedShipTo,
      s.selectedShipToId,
      s.availableShipTo,
      s.setAvailableShipTo,
      s.setSelectedShipToId,
      s.setSelectedShipTo,
      s.setSelectedShipToDc,
    ],
    shallow
  );

  const { onClose } = useDisclosure();
  const { logout } = useAuthentication();
  const queryClient = useQueryClient();
  const { data: notification } = useNotificationCount();
  const query = QueryWrapperNext('/product/keyword', access_token, {
    enabled: !!selectedShipToId,
  });
  const [stateChanged, setStateChanged] = useState<boolean>(false);
  const toast = useToast();

  const [showModalShipTo, setShowModalShipTo] = useState(
    selectedShipToId === ''
  );

  const [userRole, setUserRole] = useAuthStore(
    (s) => [s.userRole, s.setUserRole],
    shallow
  );

  const { data: productKeyword } = query.useQueryGet<ProductKeyword>(
    'ProductKeyword',
    { warehouse: selectedShipToId }
  );

  const productKeywordList = Array.isArray(productKeyword)
    ? productKeyword
    : [];

  const suggestedProduct = productKeywordList.map((val) => {
    return {
      indexId: val.index,
      label: val.name,
      url: `${val.url}&cat1=&page=1`,
    };
  });

  const suggestedProductSorted = sort(suggestedProduct).asc(
    (a: { indexId: number; label: string; url: string }) => a.indexId
  );

  const refetchUserData = async () => {
    await apiInstance(access_token)
      .get<Account>('/account/me')
      .then(({ data: userData }) => {
        if (userRole !== userData.role) {
          setStateChanged(true);
          setUserRole(userData.role);
          setCookie('buyer_active_role', userData.role);
        }

        if (availableShipTo.length !== userData.ship_tos.length) {
          setStateChanged(true);
          setAvailableShipTo(userData.ship_tos);
          setSelectedShipToId('');
          setCookie('selected_shipto_id', '');
          setCookie('selected_shipto_dc', '');
          return setShowModalShipTo(true);
        }

        if (selectedShipToId === '') {
          return setShowModalShipTo(true);
        }

        return setShowModalShipTo(false);
      })
      .catch((error) => {
        const idToast = 'error-conn';
        if (
          error instanceof AxiosError &&
          error.code === 'ERR_NETWORK' &&
          !isPwaOffline
        ) {
          if (!toast.isActive(idToast)) {
            showToast(
              toast,
              'error',
              'Maaf, tidak ada koneksi internet',
              idToast,
              'top',
              30000
            );
          }
        }
      });
  };

  const handleModalShipTo = () => {
    setShowModalShipTo(false);
    queryClient.clear();
    onClose();
  };

  useMemo(() => {
    if (availableShipTo.length === 1) {
      setShowModalShipTo(false);
      setSelectedShipToId(availableShipTo[0].id);
      setSelectedShipToDc(availableShipTo[0].distribution_channel);
      setSelectedShipTo(availableShipTo[0]);
      setAvailableShipTo(availableShipTo);

      setCookie('selected_shipto_id', availableShipTo[0].id);
      setCookie('selected_shipto_dc', availableShipTo[0].distribution_channel);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableShipTo]);

  // Delay in milliseconds
  useInterval(() => refetchUserData(), isPwaOffline ? null : 1000 * 30);

  const showBottomNav = shouldShowBuyerBottomNav(router.pathname);
  const notificationCount = notification?.count || 0;

  return (
    <>
      <NavHeaderNoSSR
        suggestedProducts={suggestedProductSorted}
        onLogout={() => logout(access_token)}
        userLogin={selectedShipTo?.search_term_shipto}
        shipTo={availableShipTo}
        selectedShipToId={selectedShipToId}
        setSelectedShipToId={setSelectedShipToId}
        showBackButton={router.pathname !== '/'}
        notificationCount={notificationCount}
      />
      <Box
        as={PageContainer}
        className={containerClassname}
        px={BUYER_LAYOUT_HORIZONTAL_PADDING}
        pt={3}
        pb={
          showBottomNav ? { base: `${BOTTOM_NAV_HEIGHT}px`, md: 0 } : undefined
        }
        transition="padding .3s"
      >
        {children}
        <Footer />
      </Box>
      {showBottomNav ? (
        <BuyerBottomNav notificationCount={notificationCount} />
      ) : null}

      <Modal
        title="Pilih Ship To"
        name="select-shipto"
        isOpen={showModalShipTo}
        scrollBehavior="inside"
        onClose={onClose}
        onSubmit={onClose}
        hideAction
        hideCancel
        hideClose
        size="xl"
      >
        <OptionShipTo shipTos={availableShipTo} onSubmit={handleModalShipTo} />
      </Modal>

      <Modal
        isCentered
        title="Pemberitahuan"
        name="state-changed"
        isOpen={stateChanged}
        onClose={onClose}
        onSubmit={() => {
          setStateChanged(false);
          queryClient.clear();
        }}
        actionButtonPosition="center"
        labelSubmit="Lanjutkan"
        hideCancel
        hideClose
        size="md"
      >
        <div>Terdapat perubahan Role atau ShipTo pada akun Anda!</div>
      </Modal>
    </>
  );
}
